// src/services/nftFetcher/bscNFTFetcher.js
import secureFetch from '../../utils/secureFetch';
import { standardizeNFTFormat } from './nftFormatUtils';

/**
 * Fetch NFTs for a BSC wallet using the Moralis API.
 * Transforms the response to match the expected format
 *
 * @param {string} walletAddress - The wallet address to query
 * @returns {Promise<Array>} - A promise that resolves to an array of NFT objects
 */
export const fetchNFTs = async (walletAddress) => {
  const API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6ImMxZmIxMzNiLThjMWEtNDYzMi05YjU5LWNhNWI0YmMzYjIwZiIsIm9yZ0lkIjoiNDI1MTY2IiwidXNlcklkIjoiNDM3Mjg3IiwidHlwZSI6IlBST0pFQ1QiLCJ0eXBlSWQiOiJhMWQ3YjZkMC03NTc0LTQ1ZDEtOTZiNC0wMGM2NjU5NmZlNGQiLCJpYXQiOjE3NDU4NzkwMTYsImV4cCI6NDkwMTYzOTAxNn0.9Gl8TWkknMJ6ld3YPJ14XoskdXYBksytD7O9uxEJ0Vk";
  
  try {
    const response = await secureFetch(
      `https://deep-index.moralis.io/api/v2/${walletAddress}/nft?chain=bsc&format=decimal&limit=50`,
      {
        headers: {
          "Accept": "application/json",
          "X-API-Key": API_KEY
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Check if the response has the expected structure
    if (!data || !Array.isArray(data.result)) {
      console.error("Unexpected Moralis NFT API response format:", data);
      return [];
    }
    
    // Transform the data to match our standardized format
    const nfts = data.result.map((item) => {
      try {
        // Parse metadata if it exists
        let parsedMetadata = null;
        if (item.metadata) {
          try {
            parsedMetadata = typeof item.metadata === 'string' 
              ? JSON.parse(item.metadata) 
              : item.metadata;
          } catch (metadataError) {
            console.warn('Failed to parse NFT metadata:', metadataError);
          }
        }
        
        // Create NFT object in format compatible with standardizeNFTFormat
        const nftData = {
          // Use Moralis format adapted to our standardization function
          tokenAddress: item.token_address,
          tokenId: item.token_id,
          collectionName: item.name || parsedMetadata?.name || 'Unknown Collection',
          collectionSymbol: item.symbol,
          chainId: 56, // BSC chain ID
          contractType: item.contract_type, // ERC-721 or ERC-1155
          metadata: parsedMetadata
        };
        
        const standardizedNFT = standardizeNFTFormat(nftData, 'bsc');
        
        // Add additional Moralis-specific data
        if (standardizedNFT) {
          // Override with Moralis metadata if available and more complete
          if (parsedMetadata) {
            if (parsedMetadata.name && !standardizedNFT.nft.name.includes(parsedMetadata.name)) {
              standardizedNFT.nft.name = parsedMetadata.name;
            }
            if (parsedMetadata.description) {
              standardizedNFT.nft.description = parsedMetadata.description;
            }
            if (parsedMetadata.image) {
              standardizedNFT.nft.image = parsedMetadata.image;
              standardizedNFT.nft.imageUrl = parsedMetadata.image;
            }
            if (parsedMetadata.attributes) {
              standardizedNFT.nft.attributes = parsedMetadata.attributes;
            }
            if (parsedMetadata.external_url) {
              standardizedNFT.nft.externalUrl = parsedMetadata.external_url;
            }
            if (parsedMetadata.animation_url) {
              standardizedNFT.nft.animationUrl = parsedMetadata.animation_url;
            }
          }
          
          // Add contract type info
          standardizedNFT.nft.contractType = item.contract_type;
          standardizedNFT.nft.type = item.contract_type || 'ERC-721';
          
          // Add BSC-specific metadata
          standardizedNFT.metadata.moralisData = {
            tokenHash: item.token_hash,
            contractType: item.contract_type,
            synced: item.synced_at,
            possible_spam: item.possible_spam || false,
            verified_collection: item.verified_collection || false
          };
          
          // Mark as potentially spam if detected
          if (item.possible_spam) {
            standardizedNFT.nft.possibleSpam = true;
          }
          
          // Mark as verified collection
          if (item.verified_collection) {
            standardizedNFT.nft.verifiedCollection = true;
          }
        }
        
        return standardizedNFT;
      } catch (error) {
        console.error("Error processing BSC NFT item:", error, item);
        return null;
      }
    }).filter(nft => nft !== null);
    
    console.log(`Fetched ${nfts.length} NFTs for BSC address ${walletAddress}`);
    return nfts;
    
  } catch (error) {
    console.error("Error fetching BSC NFTs:", error);
    return [];
  }
};